'use client'

import { useEffect, useState, type ReactNode } from 'react'

import { Loader2Icon, MusicIcon, PlusIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Shine } from '@/components/ui/shine'

const Generate = ({ children, label }: { children: ReactNode, label: string }) => {
  const [isGenerated, setIsGenerated] = useState<boolean>(false)
  const [isStarted, setIsStarted] = useState<boolean>(false)

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = []

    const runCycle = () => {
      setIsStarted(false)
      setIsGenerated(false)

      // Clear any existing timeouts
      timeouts.forEach(timeout => clearTimeout(timeout))
      timeouts.length = 0

      // 2000ms: set isStarted to true
      timeouts.push(setTimeout(() => setIsStarted(true), 2000))

      // 6000ms: set isStarted to false and isGenerated to true
      timeouts.push(
        setTimeout(() => {
          setIsStarted(false)
          setIsGenerated(true)
        }, 6000)
      )

      // 12700ms: set isGenerated to false
      timeouts.push(setTimeout(() => setIsGenerated(false), 12700))
    }

    // Run the cycle immediately
    runCycle()

    // Repeat every 12.7 seconds
    const interval = setInterval(runCycle, 12700)

    return () => {
      clearInterval(interval)
      timeouts.forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  return (
    <div className='bg-muted relative mx-2.5 mt-2.5 flex h-75 flex-col overflow-hidden rounded-xl'>
      <Shine loop deg={-45} duration={2000} className='size-full' enable={isStarted}>
        {/* A page gaining a block, drawn rather than screenshotted. The
            registry showed shadcn's own UI here; what this card is about is a
            Fuse page, so that is what it draws. The `isGenerated` state adds
            the music block the prompt asks for. */}
        {/* A real page, on the app's own dark ground, gaining the block the
            prompt asks for. Drawn rather than screenshotted so it cannot go
            stale, and coloured rather than grey so it reads as a page somebody
            made instead of a wireframe. */}
        <div className='flex h-full w-full items-start justify-center pt-6'>
          <div className='w-44 rounded-2xl border border-border bg-neutral-950 p-2.5 shadow-lg'>
            <div className='mx-auto size-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500' />
            <div className='mx-auto mt-1.5 h-1.5 w-14 rounded-full bg-white/20' />
            <div className='mt-2.5 grid grid-cols-2 gap-1.5'>
              <div className='col-span-2 h-9 rounded-lg bg-gradient-to-r from-sky-500/80 to-indigo-600/80' />
              <div className='h-9 rounded-lg bg-neutral-800' />
              <div className='h-9 rounded-lg bg-neutral-800' />
              {/* The block the prompt just added. It slides in rather than
                  appearing, so the card reads as something happening. */}
              <div
                className={`col-span-2 flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 px-2 text-white transition-all duration-500 ${
                  isGenerated ? 'h-9 opacity-100' : 'h-0 opacity-0'
                }`}
              >
                <MusicIcon className='size-3.5 shrink-0' />
                <span className='truncate font-gilroy text-[10px] font-semibold'>Late night mix</span>
              </div>
            </div>
          </div>
        </div>
      </Shine>
      <div className='from-card pointer-events-none absolute inset-x-0 bottom-0 z-11 h-10 bg-linear-to-t to-transparent' />
      <div className='border-primary bg-background absolute bottom-2 z-20 flex w-full max-w-93 justify-between self-center rounded-xl border-2 px-3 py-2'>
        {children}
        {/* One label in both states, so the button keeps its width while the
            spinner runs; `min-w` rather than a fixed width because "Добавить"
            does not fit the registry's `w-30`. */}
        <Button size='lg' className='min-w-30' disabled={isStarted}>
          {isStarted ? <Loader2Icon className='animate-spin' /> : <PlusIcon />}
          {label}
        </Button>
      </div>
    </div>
  )
}

export default Generate
